import React from 'react';
import Space from 'antd/lib/space';
import Switch from 'antd/lib/switch';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import Button from 'antd/lib/button';
import { MinusCircleFilled, NumberOutlined, SettingFilled, TeamOutlined } from '@ant-design/icons';
import { antGrey } from '../styles/globalStyles';
import { InlineStepConfigDescription } from '../common/InlineStepConfigDescription';
import { StepGraphStep } from '@/types/workflow';
import { AppNotificationType, AppPlatform, AppStoreType, StepNotificationConfigOverride } from '../../types';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import Popconfirm from 'antd/lib/popconfirm';
import { AppSettingsV2Updatable } from '../../actions/updatable';
import { useIsLoading } from '../../utils/hooks/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { tryUpdateAppSettingsV2 } from '../../actions/appSettings';
import { RootState } from '../../reducers/reducers';
import { selectUserCanPerformAction } from '../../selectors/selectors';

interface NotificationStepConfigOverrideRowProps {
    appId: string;
    orgId: string;
    workflowId: string;
    notificationId: AppNotificationType;
    stepGraphStep: StepGraphStep;
    override: StepNotificationConfigOverride;
    enabled: boolean;
    // If true, disables the toggle so the user can't fire repeated requests for the same key and trigger races with the loading-state reducer.
    isUpdating?: boolean;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    archetypeMinWidth?: number;
    availableChannelsById?: Record<string, string>;
    onClickSettings: () => void;
    onToggleEnabled?: (enabled: boolean) => void;
}

const computeOverrideIndicators = (override: StepNotificationConfigOverride) => {
    const hasChannelOverride = (override.channels?.length ?? 0) > 0;
    const hasMentionOverride =
        (override.settings?.mentionGroups?.length ?? 0) > 0 || (override.settings?.mentionUsers?.length ?? 0) > 0;
    return { hasChannelOverride, hasMentionOverride };
};

const NotificationStepConfigOverrideRow: React.FC<NotificationStepConfigOverrideRowProps> = ({
    appId,
    orgId,
    workflowId,
    notificationId,
    stepGraphStep,
    override,
    enabled,
    isUpdating,
    platform,
    appStoreType,
    archetypeMinWidth,
    availableChannelsById,
    onClickSettings,
    onToggleEnabled,
}) => {
    const dispatch = useDispatch();
    const deleteKey = `${notificationId}#${stepGraphStep.stepConfigId}`;
    const isDeleting = useIsLoading(AppSettingsV2Updatable.DeleteStepNotificationConfig, deleteKey);
    const userCanToggle = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateAppSettings, undefined, null),
    );

    const resolveChannelName = (c: { id: string; name?: string }) => availableChannelsById?.[c.id] ?? c.name ?? c.id;

    const handleDelete = () => {
        dispatch(
            tryUpdateAppSettingsV2(
                AppSettingsV2Updatable.DeleteStepNotificationConfig,
                { orgId, appId, workflowId, stepConfigId: stepGraphStep.stepConfigId, notificationId },
                deleteKey,
            ),
        );
    };

    const { hasChannelOverride, hasMentionOverride } = computeOverrideIndicators(override);
    const hasAnyOverrideIcon = hasChannelOverride || hasMentionOverride;

    const tooltipLabelStyle: React.CSSProperties = { color: antGrey.grey5 };
    const channelTooltip = (
        <div>
            <div style={tooltipLabelStyle}>Channel override:</div>
            {(override.channels?.length ?? 0) > 0 ?
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    {(override?.channels ?? []).map((c) => (
                        <li key={c.id}>{resolveChannelName(c)}</li>
                    ))}
                </ul>
            :   <div style={{ marginTop: 4 }}>(cleared)</div>}
        </div>
    );
    const mentionItems = [...(override.settings?.mentionGroups ?? []), ...(override.settings?.mentionUsers ?? [])];
    const mentionTooltip = (
        <div>
            <div style={tooltipLabelStyle}>@mention:</div>
            {mentionItems.length > 0 ?
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    {mentionItems.map((m, i) => (
                        <li key={i}>{m}</li>
                    ))}
                </ul>
            :   <div style={{ marginTop: 4 }}>(cleared)</div>}
        </div>
    );
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow: 1,
                backgroundColor: antGrey.grey2,
                padding: '8px 12px',
            }}
        >
            <InlineStepConfigDescription
                stepGraphStep={stepGraphStep}
                platform={platform}
                appStoreType={appStoreType}
                isWorkflowsCIStepEnabled // always true for step config notification overrides
                archetypeMinWidth={archetypeMinWidth}
            />
            <Space size={12} align="center">
                <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center' }}>
                    {hasChannelOverride && (
                        <Tooltip
                            title={channelTooltip}
                            placement="leftTop"
                            overlayStyle={{ maxWidth: 1000 }}
                            mouseEnterDelay={0}
                            mouseLeaveDelay={0}
                        >
                            <NumberOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                        </Tooltip>
                    )}
                </span>
                <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center' }}>
                    {hasMentionOverride && (
                        <Tooltip
                            title={mentionTooltip}
                            placement="leftTop"
                            overlayStyle={{ maxWidth: 1000 }}
                            mouseEnterDelay={0}
                            mouseLeaveDelay={0}
                        >
                            <TeamOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                        </Tooltip>
                    )}
                </span>
                <Divider
                    type="vertical"
                    style={{
                        margin: '0 4px',
                        height: 20,
                        borderColor: antGrey.grey6,
                        visibility: hasAnyOverrideIcon ? 'visible' : 'hidden',
                    }}
                />
                {userCanToggle ?
                    <Switch
                        checked={enabled}
                        disabled={!onToggleEnabled || isUpdating}
                        onChange={(checked, event) => {
                            event.stopPropagation();
                            onToggleEnabled?.(checked);
                        }}
                    />
                :   <Tooltip title={DISALLOWED_ACTION_ERROR_MESSAGE} placement="topRight">
                        <span>
                            <Switch checked={enabled} disabled />
                        </span>
                    </Tooltip>
                }
                <Button
                    type="text"
                    icon={<SettingFilled style={{ color: antGrey.grey7 }} />}
                    onClick={onClickSettings}
                />
                <Popconfirm
                    title={`Remove override for ${stepGraphStep.stepName}?`}
                    description="The notification will use app-level defaults."
                    onConfirm={handleDelete}
                    okText="Remove"
                    okButtonProps={{ danger: true, disabled: !userCanToggle }}
                    placement="leftTop"
                >
                    <Button
                        type="text"
                        loading={isDeleting}
                        icon={<MinusCircleFilled style={{ color: antGrey.grey7 }} />}
                    />
                </Popconfirm>
            </Space>
        </div>
    );
};

export default NotificationStepConfigOverrideRow;
